import React, { useState, useEffect } from 'react';
import { 
    registrarConsulta, 
    editarConsulta, 
    obtenerConsultas,
    eliminarConsulta 
} from '../services/chatbotService';
import { obtenerCentro } from '../services/centroService';
import { 
    Plus, 
    Edit2, 
    Trash2, 
    Save, 
    X, 
    Search,
    AlertCircle,
    MessageSquare
} from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ROLES = ['Administrador', 'Profesor', 'Padre'];

// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const Button = ({ children, onClick, className = '', variant = 'primary', size = 'default' }) => {
    const baseStyles = 'rounded-lg font-medium transition-all duration-300 flex items-center justify-center shadow-sm';
    
    const variantStyles = {
        primary: 'bg-indigo-500 text-white hover:bg-indigo-600',
        ghost: 'bg-transparent',
        green: 'bg-emerald-500 text-white hover:bg-emerald-600',
        danger: 'bg-rose-500 text-white hover:bg-rose-600',
        blue: 'bg-blue-500 text-white hover:bg-blue-600'
    };

    const sizeStyles = {
        default: 'px-4 py-2 text-sm',
        sm: 'px-3 py-1.5 text-xs',
        lg: 'px-6 py-3 text-base',
        icon: 'p-2',
        xs: 'p-1 text-xs'
    };

    return (
        <button
            onClick={onClick}
            className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
        >
            {children}
        </button>
    );
};

const GestionConsultas = () => {
    const [consultas, setConsultas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modo, setModo] = useState('lista'); 
    const [searchTerm, setSearchTerm] = useState('');
    const [consultaActual, setConsultaActual] = useState({
        pregunta: '',
        respuesta: '',
        rol: '',
        id_consulta: null,
        incidencia:0
    });

    useEffect(() => {
        cargarConsultas();
    }, []);

    const cargarConsultas = async () => {
        setLoading(true);
        try {
            const response = await obtenerConsultas();
            if (response.success) {
                setConsultas(response.data);
            } else {
                throw new Error(response.message || 'Error al cargar consultas');
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const centroResponse = await obtenerCentro();
            if (!centroResponse.success || !centroResponse.data || centroResponse.data.length === 0) {
                throw new Error('No se pudo obtener el ID del centro');
            }

            const idCentro = centroResponse.data[0]?.id_centro;
            if (!idCentro) {
                throw new Error('ID del centro no disponible');
            }

            if (modo === 'nueva') {
                await registrarConsulta(
                    consultaActual.pregunta,
                    consultaActual.respuesta,
                    idCentro,
                    consultaActual.rol, 
                    0
                );
                showSuccessToast('Consulta registrada exitosamente');
            } else if (modo === 'editar') {
                await editarConsulta(
                    consultaActual.id_consulta,
                    consultaActual.pregunta,
                    consultaActual.respuesta,
                    idCentro,
                    consultaActual.rol,
                    consultaActual.incidencia
                );
                showSuccessToast('Consulta editada exitosamente');
            }

            await cargarConsultas();
            setModo('lista');
            setConsultaActual({ pregunta: '', respuesta: '', rol: '', id_consulta: null });
        } catch (error) {
            setError(error.message);
            showErrorToast(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar esta consulta?')) {
            setLoading(true);
            setError(null);
            try {
                await eliminarConsulta(id);
                await cargarConsultas();
                showSuccessToast('Consulta eliminada exitosamente');
            } catch (error) {
                setError(error.message);
                showErrorToast(error.message);
            } finally {
                setLoading(false);
            }
        }
    };

    const filteredConsultas = consultas.filter(consulta => 
        consulta.pregunta.toLowerCase().includes(searchTerm.toLowerCase()) ||
        consulta.respuesta.toLowerCase().includes(searchTerm.toLowerCase()) ||
        consulta.rol.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const ordenarConsultas = (campo) => {
        const consultasOrdenadas = [...consultas].sort((a, b) => {
            if (a[campo] < b[campo]) return -1;
            if (a[campo] > b[campo]) return 1;
            return 0;
        });
        setConsultas(consultasOrdenadas);
    };

    return (
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ToastContainer 
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                className="mt-16"
                style={{ zIndex: 9999 }}
            />
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4 sm:mb-6">
                <div className="p-4 sm:p-6 border-b border-slate-200">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-xl sm:text-2xl font-semibold text-slate-900">Gestión de Consultas</h1>
                            <p className="text-sm text-slate-500 mt-1">Administra las consultas del chatbot</p>
                        </div>
                        {modo === 'lista' && (
                            <Button
                                onClick={() => {
                                    setModo('nueva');
                                    setConsultaActual({ pregunta: '', respuesta: '', rol: '', id_consulta: null });
                                }}
                                variant="primary"
                                className="w-full sm:w-auto"
                            >
                                <Plus size={20} className="mr-2" />
                                Nueva Consulta
                            </Button>
                        )}
                    </div>

                    <div className="mt-4">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Buscar consultas..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm"
                            />
                            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="m-4 p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg flex items-center gap-2">
                        <AlertCircle size={20} />
                        <span className="text-sm">{error}</span>
                    </div>
                )}

                {modo === 'lista' ? (
                    <div className="divide-y divide-slate-200">
                        {/* Vista móvil - Cards */}
                        <div className="block sm:hidden">
                            {filteredConsultas.map((consulta, index) => (
                                <div key={consulta.id_consulta} 
                                     className="p-4 border-b last:border-b-0 hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center space-x-4 mb-3">
                                        <div className="bg-indigo-100 rounded-full p-2">
                                            <MessageSquare className="h-5 w-5 text-indigo-600" />
                                        </div>
                                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                                            {consulta.rol}
                                        </span>
                                    </div>
                                    <div className="space-y-2 mb-4">
                                        <div>
                                            <h3 className="font-semibold text-slate-900">Pregunta:</h3>
                                            <p className="text-sm text-slate-700">{consulta.pregunta}</p>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-slate-900">Respuesta:</h3>
                                            <p className="text-sm text-slate-700">{consulta.respuesta}</p>
                                        </div>
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <Button
                                            onClick={() => {
                                                setConsultaActual(consulta);
                                                setModo('editar');
                                            }}
                                            variant="ghost"
                                            size="sm"
                                            className="p-1 text-gray-400 hover:text-indigo-500 transition-colors"
                                        >
                                            <Edit2 className="h-5 w-5" />
                                        </Button>
                                        <Button
                                            onClick={() => handleDelete(consulta.id_consulta)}
                                            variant="ghost"
                                            size="sm"
                                             className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                        >
                                            <Trash2 className="h-5 w-5" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Vista desktop - Tabla */}
                        <div className="hidden sm:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th 
                                            className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-indigo-600 w-[30%]"
                                            onClick={() => ordenarConsultas('pregunta')}
                                        >
                                            Pregunta <span className="ml-1">↑↓</span>
                                        </th>
                                        <th 
                                            className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-indigo-600 w-[40%]"
                                            onClick={() => ordenarConsultas('respuesta')}
                                        >
                                            Respuesta <span className="ml-1">↑↓</span>
                                        </th>
                                        <th 
                                            className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-indigo-600 w-[15%]"
                                            onClick={() => ordenarConsultas('rol')}
                                        >
                                            Rol <span className="ml-1">↑↓</span>
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider w-[15%]">
                                            Acciones
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-slate-200">
                                    {filteredConsultas.map((consulta, index) => (
                                        <tr key={consulta.id_consulta} className="hover:bg-slate-50 transition-colors">
                                            <td className="px-6 py-4 text-sm text-slate-900">
                                                <div className="line-clamp-2 break-words">
                                                    {consulta.pregunta}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-slate-900">
                                                <div className="line-clamp-2 break-words">
                                                    {consulta.respuesta}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                    {consulta.rol}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right whitespace-nowrap">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        onClick={() => {
                                                            setConsultaActual(consulta);
                                                            setModo('editar');
                                                        }}
                                                        variant="ghost"
                                                        size="sm"
                                                        className="p-1 text-gray-400 hover:text-indigo-500 transition-colors"
                                                    >
                                                        <Edit2 className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleDelete(consulta.id_consulta)}
                                                        variant="ghost"
                                                        size="sm"
                                                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 className="h-4 w-4"/>
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="p-4 sm:p-6">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    Pregunta
                                </label>
                                <input
                                    type="text"
                                    value={consultaActual.pregunta}
                                    onChange={(e) => setConsultaActual({
                                        ...consultaActual,
                                        pregunta: e.target.value
                                    })}
                                    className="w-full rounded-lg border border-slate-200 bg-white py-2 px-3 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    Respuesta
                                </label>
                                <textarea
                                    value={consultaActual.respuesta}
                                    onChange={(e) => setConsultaActual({
                                        ...consultaActual,
                                        respuesta: e.target.value
                                    })}
                                    className="w-full rounded-lg border border-slate-200 bg-white py-2 px-3 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm h-32"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    Rol
                                </label>
                                <select
                                    value={consultaActual.rol}
                                    onChange={(e) => setConsultaActual({
                                        ...consultaActual,
                                        rol: e.target.value
                                    })}
                                    className="w-full rounded-lg border border-slate-200 bg-white py-2 px-3 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm"
                                    required
                                >
                                    <option value="">Seleccione un rol</option>
                                    {ROLES.map(rol => (
                                        <option key={rol} value={rol}>{rol}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 pt-4">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={loading}
                                    className="w-full sm:w-auto"
                                >
                                    <Save size={20} className="mr-2" />
                                    {loading ? 'Guardando...' : 'Guardar'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => {
                                        setModo('lista');
                                        setConsultaActual({ pregunta: '', respuesta: '', rol: '', id_consulta: null });
                                    }}
                                    className="w-full sm:w-auto"
                                >
                                    <X size={20} className="mr-2" />
                                    Cancelar
                                </Button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GestionConsultas;